import {
    Calendar,
    Clock,
    Hash,
    Mail,
    MapPin,
    MessageSquare,
    Phone,
    User,
    Users,
    XCircle
} from 'lucide-react';
import { formatStatusBadge } from '../../components/ui/table-helpers';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

interface ReservationDetailsDialogProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly reservation: {
    readonly id: string;
    readonly bookedId: string;
    readonly customerName: string;
    readonly customerEmail: string;
    readonly customerPhone: string;
    readonly date: string;
    readonly time: string;
    readonly guestNo: number;
    readonly specialRequests?: string;
    readonly status: string;
    readonly createdAt: string;
    readonly updatedAt: string;
    readonly tableId?: string;
    readonly tableNo?: number;
    readonly totalAmount?: number;
    readonly notes?: string;
  } | null;
  readonly onActionClick?: (action: string) => void;
}


const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long'
  });
};

const formatDateTime = (dateString: string) => {
  return new Date(dateString).toLocaleString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-LK', {
    style: 'currency',
    currency: 'LKR'
  }).format(amount);
};

export default function ReservationDetailsDialog({ 
  isOpen, 
  onClose, 
  reservation, 
  onActionClick 
}: ReservationDetailsDialogProps) {
  if (!reservation) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Reservation Details</span>
            {formatStatusBadge(reservation.status)}
          </DialogTitle>
          <DialogDescription>
            View reservation information, customer details, and booking status
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Customer Information */}
          <div className="bg-slate-100 dark:bg-slate-800/50 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <User className="h-5 w-5 mr-2" />
              Customer Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Name</div>
                  <div className="font-medium">{reservation.customerName}</div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Email</div>
                  <div className="font-medium">{reservation.customerEmail}</div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Phone</div>
                  <div className="font-medium">{reservation.customerPhone}</div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Hash className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Booking ID</div>
                  <div className="font-medium font-mono text-sm">{reservation.bookedId}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Reservation Details */}
          <div className="bg-blue-100 dark:bg-blue-900/20 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <Calendar className="h-5 w-5 mr-2" />
              Reservation Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Date</div>
                  <div className="font-medium">{formatDate(reservation.date)}</div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Time</div>
                  <div className="font-medium">{reservation.time}</div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Guest Count</div>
                  <div className="font-medium">{reservation.guestNo} guests</div>
                </div>
              </div>
              {reservation.tableId && (
                <div className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="text-sm text-muted-foreground">Table</div>
                    <div className="font-medium">Table {reservation.tableId}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Special Requests */}
          {reservation.specialRequests && (
            <div className="bg-amber-100 dark:bg-amber-900/20 rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-3 flex items-center">
                <MessageSquare className="h-5 w-5 mr-2" />
                Special Requests
              </h3>
              <p className="text-gray-700 dark:text-gray-300">{reservation.specialRequests}</p>
            </div>
          )}

          {/* Notes */}
          {reservation.notes && (
            <div className="bg-gray-100 dark:bg-gray-800/50 rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-3">Internal Notes</h3>
              <p className="text-gray-700 dark:text-gray-300">{reservation.notes}</p>
            </div>
          )}

          {/* Financial Information */}
          {reservation.totalAmount && (
            <div className="bg-green-100 dark:bg-green-900/20 rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-3">Payment Information</h3>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(reservation.totalAmount)}
              </div>
            </div>
          )}

          {/* System Information */}
          <div className="bg-gray-100 dark:bg-gray-800/50 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-3">System Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Created</div>
                <div className="font-medium">{formatDateTime(reservation.createdAt)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Last Updated</div>
                <div className="font-medium">{formatDateTime(reservation.updatedAt)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Reservation ID</div>
                <div className="font-medium font-mono">#{reservation.id}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Status</div>
                <div className="font-medium capitalize">{reservation.status}</div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2 pt-4 border-t">
            {reservation.status === 'accepted' && onActionClick && (
              <Button
                variant="outline"
                className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                onClick={() => onActionClick('cancel')}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Cancel Reservation
              </Button>
            )}
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}